import api from './api'
import {
  ServicePackageFormData,
  ServicePackageListItem,
  FileAttachment,
} from '../types/servicePackage'
import {
  payers,
  reportTypes,
  specialties,
  bodyRegions,
} from '../data/servicePackageMockData'

export interface ServicePackagePayload {
  name: string
  specialization?: string | null
  short_description?: string
  long_description?: string
  therapist_id: string
  license_id?: string | null
  // discipline?: string
  modality?: string[]
  min_age?: number | null
  max_age?: number | null
  language?: string[]
  targetGender?: string[]
  cpt_code?: Array<{
    _id: string
    cpt_code: string
    description: string
    quantity: string
  }>
  icd_code?: string[]
  necessity_note?: string
  hcpcs_level?: string[]
  modifier?: string
  pos?: string
  private_pay_rate?: number | null
  is_accept_insurance?: boolean
  insurances?: string[]
  session_length?: number
  session_buffer?: number
  travelOnsiteSurcharge?: number | null
  cancellationWindowHours?: number | null
  noShowFee?: number | null
  lateCancellationFee?: number | null
  service_radius?: number | null
  service_zipcode?: string[]
  requeried_report?: string[]
  export_format?: string | string[]
  document?: string[]
  specility?: string[]
  body_regions?: string[]
  tags?: string[]
  accept_insurance_in_search?: boolean
  // show_in_search?: boolean
  // states?: string[]
  is_active?: boolean
  status: 'draft' | 'published' | 'archived'
  isDeleted?: boolean
}

export interface CptCodeOption {
  id: string
  code: string
  description: string
}

export interface IcdCodeOption {
  id: string
  code: string
  description: string
}

export interface HCPCSCodeOption {
  id: string
  code: string
  description: string
  serviceType?: string
}

export interface ModifierOption {
  id: string
  code: string
  description: string
  serviceType?: string
}

export interface SpecializationOption {
  id: string
  name: string
  description?: string
}

const normalizeString = (value: unknown): string => {
  if (typeof value === 'string') return value.trim()
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return (value as any)._id || (value as any).id || ''
  }
  return value != null ? String(value) : ''
}

const mapValuesToIds = (
  values: unknown,
  options: Array<{ id: string; name: string }>
): string[] => {
  if (!Array.isArray(values)) return []

  return values
    .map(value => {
      const normalized = normalizeString(value)
      if (!normalized) return null

      const byId = options.find(option => option.id === normalized)
      if (byId) return byId.id

      const byName = options.find(
        option => option.name.toLowerCase() === normalized.toLowerCase()
      )
      return byName ? byName.id : normalized
    })
    .filter((value): value is string => Boolean(value))
}

const mapDocumentsToAttachments = (documents: unknown): FileAttachment[] => {
  if (!Array.isArray(documents)) return []

  return documents
    .map((doc, index) => {
      if (!doc) return null

      if (typeof doc === 'string') {
        const name = doc.split('/').pop() || `Attachment-${index + 1}`
        return {
          id: `${index}-${name}`,
          name,
          size: 0,
          type: '',
          url: doc,
        } as FileAttachment
      }

      if (typeof doc === 'object') {
        const document = doc as Record<string, any>
        const name =
          normalizeString(document.name) ||
          normalizeString(document.originalname) ||
          `Attachment-${index + 1}`
        return {
          id:
            normalizeString(document.id) ||
            normalizeString(document._id) ||
            `${index}-${name}`,
          name,
          size: Number(document.size) || 0,
          type:
            normalizeString(document.type) ||
            normalizeString(document.mimeType) ||
            '',
          url: normalizeString(document.url) || normalizeString(document.path),
        } as FileAttachment
      }

      return null
    })
    .filter(Boolean) as FileAttachment[]
}

const mapServiceResponseToFormData = (
  service: Record<string, any>
): ServicePackageFormData => {
  const cptCodes = (
    Array.isArray(service.cpt_code)
      ? service.cpt_code.map((item: any, index: number) => {
        const code =
          normalizeString(item?.cpt_code) || normalizeString(item?.code)
        if (!code) return null

        // _id is the primary identifier from API
        const codeId =
          normalizeString(item?._id) ||
          normalizeString(item?.codeId) ||
          normalizeString(item?.cpt_code_id) ||
          ''

        return {
          id:
            normalizeString(item?.id) ||
            normalizeString(item?._id) ||
            `${index}-${code}`,
          codeId, // Store _id as codeId for internal use
          code, // Store cpt_code as code
          description: normalizeString(item?.description) || code,
          quantity: Number(item?.quantity) || 1,
        } as ServicePackageFormData['clinicalCoding']['cptCodes'][number]
      })
      : []
  ).filter(Boolean) as ServicePackageFormData['clinicalCoding']['cptCodes']

  const icdCodes = (
    Array.isArray(service.icd_code)
      ? service.icd_code.map((item: any, index: number) => {
        const code =
          normalizeString(item?.code) ||
          normalizeString(item?.icd_code) ||
          normalizeString(item)
        if (!code) return null

        // Only use codeId if it exists, don't fall back to code
        // If codeId is missing, we'll need to fetch it or skip it
        const codeId =
          normalizeString(item?.codeId) ||
          normalizeString(item?.icd_code_id) ||
          normalizeString(item?._id)

        // If we don't have a codeId, try to use the item itself if it looks like an ID
        // Otherwise, we'll need to handle it during save
        const finalCodeId =
          codeId ||
          (typeof item === 'string' && item.length > 10 ? item : null)

        return {
          id:
            normalizeString(item?.id) ||
            normalizeString(item?._id) ||
            `${index}-${code}`,
          codeId: finalCodeId || code, // Fallback to code for display, but we'll filter during save
          code,
          description: normalizeString(item?.description) || code,
        } as ServicePackageFormData['clinicalCoding']['icd10Codes'][number]
      })
      : []
  ).filter(Boolean) as ServicePackageFormData['clinicalCoding']['icd10Codes']

  const hcpcsCodes = (
    Array.isArray(service.hcpcs_level)
      ? service.hcpcs_level.map((item: any, index: number) => {
        const code =
          normalizeString(item?.code) ||
          normalizeString(item?.hcpcs_code) ||
          normalizeString(item)
        if (!code) return null

        const codeId =
          normalizeString(item?.codeId) ||
          normalizeString(item?.hcpcs_code_id) ||
          normalizeString(item?._id) ||
          (typeof item === 'string' && item.length > 10 ? item : null)

        return {
          id:
            normalizeString(item?.id) ||
            normalizeString(item?._id) ||
            `${index}-${code}`,
          codeId: codeId || code,
          code,
          description: normalizeString(item?.description) || code,
        } as ServicePackageFormData['clinicalCoding']['hcpcsLevel'][number]
      })
      : []
  ).filter(Boolean) as ServicePackageFormData['clinicalCoding']['hcpcsLevel']

  // Extract modifier ID - handle both object and string formats
  const modifier =
    typeof service.modifier === 'object' && service.modifier !== null
      ? normalizeString(service.modifier._id || service.modifier.id) || ''
      : normalizeString(service.modifier) || ''
  const pos = normalizeString(service.pos) || ''

  const reportsRequired = mapValuesToIds(
    service.requeried_report ?? service.requiredReports,
    reportTypes
  )

  const specialtyIds = mapValuesToIds(
    service.specility ?? service.specialities ?? service.specialties,
    specialties
  )

  const bodyRegionIds = mapValuesToIds(
    service.body_regions ?? service.bodyRegions,
    bodyRegions
  )

  // Handle new structure where insurances is array of objects with _id and payer_name
  // or old structure with array of IDs/names
  const insuranceData = service.insurances ?? service.acceptedPayers ?? []

  // Preserve the full insurance objects if they exist (for edit mode)
  const insuranceObjects = Array.isArray(insuranceData)
    ? insuranceData.filter(
      (item): item is { _id: string; payer_name: string } =>
        typeof item === 'object' &&
        item !== null &&
        typeof item._id === 'string' &&
        typeof item.payer_name === 'string'
    )
    : []

  const acceptedPayerIds = Array.isArray(insuranceData)
    ? insuranceData
      .map(item => {
        // If it's an object with _id and payer_name, use the _id
        if (typeof item === 'object' && item !== null && item._id) {
          return normalizeString(item._id)
        }
        // If it's a string, check if it looks like an ID (MongoDB ObjectId is 24 hex chars)
        // or if it's a short string (likely a name), we'll need to look it up
        if (typeof item === 'string') {
          const trimmed = item.trim()
          // MongoDB ObjectId format: 24 hexadecimal characters
          if (/^[0-9a-fA-F]{24}$/.test(trimmed)) {
            return trimmed
          }
          // If it's not an ID format, it's likely a name - return null to skip it
          // The user will need to re-select it, or we could look it up here
          return null
        }
        return null
      })
      .filter((id): id is string => Boolean(id))
    : []

  const attachments = mapDocumentsToAttachments(
    service.document ?? service.documents
  )

  const modality = Array.isArray(service.modality)
    ? service.modality.map(normalizeString).filter(Boolean)
    : service.modality
      ? [normalizeString(service.modality)]
      : ['in_person']

  const languages = Array.isArray(service.language)
    ? service.language.map(normalizeString).filter(Boolean)
    : []

  const keywords = Array.isArray(service.tags)
    ? service.tags.map(normalizeString).filter(Boolean)
    : []

  const states = Array.isArray(service.states)
    ? service.states.map(normalizeString).filter(Boolean)
    : []

  const visibility =
    typeof service.accept_insurance_in_search === 'boolean'
      ? service.accept_insurance_in_search
      : typeof service.show_in_search === 'boolean'
        ? service.show_in_search
        : true

  // Extract specialization ID - handle both object and string formats
  const specializationId =
    typeof service.specialization === 'object' &&
      service.specialization !== null
      ? normalizeString(service.specialization._id || service.specialization.id)
      : normalizeString(service.specialization)

  return {
    basics: {
      serviceTitle: normalizeString(service.name),
      discipline: specializationId,
      modality: modality.length > 0 ? modality : ['in_person'],
      shortDescription: normalizeString(service.short_description),
      detailedDescription: normalizeString(service.long_description),
      ageRange: {
        min:
          typeof service.min_age === 'number'
            ? service.min_age
            : service.min_age != null
              ? Number(service.min_age)
              : null,
        max:
          typeof service.max_age === 'number'
            ? service.max_age
            : service.max_age != null
              ? Number(service.max_age)
              : null,
      },
      languages,
      targetGender: service.targetGender
        ? Array.isArray(service.targetGender)
          ? service.targetGender
          : typeof service.targetGender === 'string'
            ? service.targetGender
              .split(/,\s*/)
              .map(g => g.trim())
              .filter(Boolean)
            : []
        : [],
      license_id: normalizeString(
        service.license_id ||
        (typeof service.license === 'object' && service.license !== null
          ? service.license._id || service.license.id
          : service.license)
      ),
    },
    clinicalCoding: {
      cptCodes,
      icd10Codes: icdCodes,
      medicalNecessityNote: normalizeString(
        service.necessity_note ?? service.medicalNecessityNote
      ),
      hcpcsLevel: hcpcsCodes,
      modifier,
      pos,
    },
    pricingBilling: {
      privatePayRate:
        service.private_pay_rate != null
          ? Number(service.private_pay_rate)
          : null,
      acceptInsurance:
        typeof service.is_accept_insurance === 'boolean'
          ? service.is_accept_insurance
          : Boolean(service.acceptInsurance),
      acceptedPayers: acceptedPayerIds,
      commissionPercentage:
        service.commissionPercentage != null
          ? Number(service.commissionPercentage)
          : 15,
    },
    schedulingRules: {
      sessionLengthMin:
        service.session_length != null ? Number(service.session_length) : 60,
      session_buffer:
        service.session_buffer != null ? Number(service.session_buffer) : 0,
      travelOnsiteSurcharge:
        service.travelOnsiteSurcharge != null
          ? Number(service.travelOnsiteSurcharge)
          : service.travel_onsite_surcharge != null
            ? Number(service.travel_onsite_surcharge)
            : null,
      cancellationWindowHours:
        service.cancellationWindowHours != null
          ? Number(service.cancellationWindowHours)
          : service.cancellation_window_hours != null
            ? Number(service.cancellation_window_hours)
            : null,
      noShowFee:
        service.noShowFee != null
          ? Number(service.noShowFee)
          : service.no_show_fee != null
            ? Number(service.no_show_fee)
            : null,
      lateCancellationFee:
        service.lateCancellationFee != null
          ? Number(service.lateCancellationFee)
          : service.late_cancellation_fee != null
            ? Number(service.late_cancellation_fee)
            : null,
      inPersonSettings: {
        serviceArea:
          service.serviceArea === 'fixed_zip_list'
            ? 'fixed_zip_list'
            : 'radius_miles',
        radiusMiles:
          service.service_radius != null ? Number(service.service_radius) : 10,
        zipCodes: Array.isArray(service.service_zipcode)
          ? service.service_zipcode
            .map(normalizeString)
            .filter(code => Boolean(code))
          : [],
      },
    },
    documentation: {
      reportsRequired,
      exportFormats: Array.isArray(service.export_format)
        ? service.export_format
          .map(normalizeString)
          .filter((value: string) => Boolean(value))
        : service.export_format
          ? [normalizeString(service.export_format)]
          : [],
      contractAttachments: attachments,
    },
    visibilityMatching: {
      specialties: specialtyIds,
      bodyRegions: bodyRegionIds,
      keywords,
      showInSearch: visibility,
      states,
    },
    status: (service.status as ServicePackageFormData['status']) || 'draft',
    createdAt: normalizeString(service.createdAt) || new Date().toISOString(),
    updatedAt: normalizeString(service.updatedAt) || new Date().toISOString(),
    id: normalizeString(service._id || service.id) || undefined,
    // Preserve insurance objects for edit mode initialization
    _initialInsurances:
      insuranceObjects.length > 0 ? insuranceObjects : undefined,
  }
}

export const fetchCptCodes = async (
  search: string = ''
): Promise<CptCodeOption[]> => {
  const response = await api.get('/programs/cpt-code', {
    params: search ? { search } : {},
  })

  const items = Array.isArray(response.data?.data) ? response.data.data : []

  return items
    .map((item: any) => ({
      id: item._id || item.cpt_code || `${Math.random()}`,
      code: item.cpt_code || '',
      description:
        item.description ||
        item.cpt_code ||
        (item.name ? String(item.name) : ''),
    }))
    .filter(option => option.code)
}

export const fetchIcdCodes = async (
  search: string = ''
): Promise<IcdCodeOption[]> => {
  const response = await api.get('/diagnosiscode', {
    params: search ? { search } : {},
  })

  const items = Array.isArray(response.data?.data) ? response.data.data : []

  return items
    .map((item: any) => ({
      id: item._id || item.code || `${Math.random()}`,
      code: item.code || '',
      description: item.description || item.code || '',
    }))
    .filter(option => option.code)
}

export const fetchHCPCSCodes = async (): Promise<HCPCSCodeOption[]> => {
  const response = await api.get('/services/hcpcs')

  const items = Array.isArray(response.data?.data) ? response.data.data : []

  return items
    .map((item: any) => ({
      id: item._id || item.code || `${Math.random()}`,
      code: item.code || '',
      description: item.description || item.code || '',
      serviceType: item.serviceType || '',
    }))
    .filter(option => option.code && option.id)
}

export const fetchModifiers = async (): Promise<ModifierOption[]> => {
  const response = await api.get('/services/modifiers')

  const items = Array.isArray(response.data?.data) ? response.data.data : []

  return items
    .map((item: any) => ({
      id: item._id || item.code || `${Math.random()}`,
      code: item.code || '',
      description: item.description || item.code || '',
      serviceType: item.serviceType || '',
    }))
    .filter(option => option.code && option.id)
}

export const fetchSpecializations = async (): Promise<
  SpecializationOption[]
> => {
  const response = await api.get('/specilities/specility')
  const items = Array.isArray(response.data) ? response.data : []

  return items
    .map((item: any) => ({
      id: item._id || '',
      name: item.name || '',
      description: item.description || '',
    }))
    .filter(option => option.id && option.name)
}

export const buildServicePackagePayload = (
  formData: ServicePackageFormData,
  therapistId: string
): ServicePackagePayload => {
  if (!therapistId) {
    throw new Error('Provider ID is required to submit a service package.')
  }

  const {
    basics,
    clinicalCoding,
    pricingBilling,
    schedulingRules,
    documentation,
    visibilityMatching,
    status,
  } = formData

  const cptCodesPayload =
    clinicalCoding.cptCodes
      ?.filter(code => (code.codeId || code.code) && code.code)
      .map(code => ({
        _id: code.codeId || '', // _id from API (stored as codeId)
        cpt_code: code.code, // cpt_code from API
        description: code.description || '', // description from API
        quantity: String(code.quantity ?? 1), // quantity as string
      })) ?? []

  // Only send codeId (the actual ID), not the code string
  // Filter out codes that look like ICD-10 codes (e.g., "A000", "M79.3") which are short alphanumeric strings
  const icdCodes =
    clinicalCoding.icd10Codes
      ?.map(code => {
        const codeId = code.codeId
        if (!codeId || typeof codeId !== 'string') return null

        const trimmed = codeId.trim()
        // Skip if it's too short (ICD-10 codes are typically 3-7 chars like "A000", "M79.3")
        if (trimmed.length < 10) return null
        // Skip if it matches the pattern of an ICD-10 code (e.g., starts with letter followed by numbers/dots)
        if (/^[A-Z][0-9A-Z.]+$/.test(trimmed) && trimmed.length <= 10)
          return null

        // Include if it looks like an ID (MongoDB ObjectId is 24 hex chars, or longer strings)
        return trimmed
      })
      .filter((id): id is string => Boolean(id)) ?? []

  // HCPCS Level II codes - send codeId if available, otherwise code
  const hcpcsLevelCodes =
    clinicalCoding.hcpcsLevel
      ?.map(code => {
        const codeId = code.codeId
        if (!codeId || typeof codeId !== 'string') {
          // If no codeId, use code as fallback
          return code.code || null
        }

        const trimmed = codeId.trim()
        // If it looks like an ID (MongoDB ObjectId is 24 hex chars, or longer strings)
        if (trimmed.length >= 10 && !/^[A-Z][0-9A-Z.]+$/.test(trimmed)) {
          return trimmed
        }
        // Otherwise use the code
        return code.code || null
      })
      .filter((id): id is string => Boolean(id)) ?? []

  // Modifier - send codeId if available, otherwise code
  const modifierCode = clinicalCoding.modifier
    ? (() => {
      // If modifier looks like an ID (24 hex chars), use it as is
      if (/^[0-9a-fA-F]{24}$/.test(clinicalCoding.modifier.trim())) {
        return clinicalCoding.modifier.trim()
      }
      // Otherwise, it's likely a code, use it as is
      return clinicalCoding.modifier.trim()
    })()
    : undefined

  // POS - send as is (should be "10", "11", or "12")
  const posCode = clinicalCoding.pos ? clinicalCoding.pos.trim() : undefined

  // acceptedPayers should contain payer _id values (MongoDB ObjectIds)
  // Filter out any values that look like names (e.g., "Aetna", "CIGNA") instead of IDs
  const acceptedInsuranceIds = pricingBilling.acceptedPayers.filter(
    (id): id is string => {
      if (!id || typeof id !== 'string') return false
      const trimmed = id.trim()

      // Skip if it's too short (insurance names are typically 3-20 chars)
      if (trimmed.length < 10) return false

      // Skip if it looks like a name (starts with capital letter and contains letters/spaces)
      // Insurance names like "Aetna", "CIGNA", "United Health Care" would match this
      if (/^[A-Z][a-zA-Z\s-]+$/.test(trimmed) && trimmed.length < 50)
        return false

      // Include if it looks like a MongoDB ObjectId (24 hex chars) or other ID format
      // MongoDB ObjectId: 24 hexadecimal characters
      if (/^[0-9a-fA-F]{24}$/.test(trimmed)) return true
      // Other ID formats: longer strings that don't look like names
      if (trimmed.length >= 10 && !/^[A-Z][a-zA-Z\s-]+$/.test(trimmed))
        return true

      return false
    }
  )

  const reportNames = documentation.reportsRequired
    .map(reportId => reportTypes.find(report => report.id === reportId)?.name)
    .filter((name): name is string => Boolean(name))

  const specialtyNames = visibilityMatching.specialties
    .map(
      specialtyId =>
        specialties.find(specialty => specialty.id === specialtyId)?.name ||
        specialtyId
    )
    .filter(Boolean)

  const bodyRegionNames = visibilityMatching.bodyRegions
    .map(
      regionId =>
        bodyRegions.find(region => region.id === regionId)?.name || regionId
    )
    .filter(Boolean)

  const document = documentation.contractAttachments
    .map(file => file.url || file.name)
    .filter((value): value is string => Boolean(value))

  const payload: ServicePackagePayload = {
    name: basics.serviceTitle,
    specialization: basics.discipline || null,
    short_description: basics.shortDescription,
    long_description: basics.detailedDescription,
    therapist_id: therapistId,
    license_id: basics.license_id || null,
    targetGender:
      basics.targetGender && basics.targetGender.length > 0
        ? basics.targetGender
        : undefined,
    // discipline: basics.discipline,
    modality: basics.modality,
    min_age: basics.ageRange.min ?? undefined,
    max_age: basics.ageRange.max ?? undefined,
    language: basics.languages,
    cpt_code: cptCodesPayload.length ? cptCodesPayload : [],
    icd_code: icdCodes.length ? icdCodes : [],
    necessity_note: clinicalCoding.medicalNecessityNote,
    hcpcs_level: hcpcsLevelCodes.length ? hcpcsLevelCodes : undefined,
    modifier: modifierCode,
    pos: posCode,
    private_pay_rate: pricingBilling.privatePayRate ?? undefined,
    is_accept_insurance: pricingBilling.acceptInsurance,
    insurances: acceptedInsuranceIds.length > 0 ? acceptedInsuranceIds : [],
    session_length: schedulingRules.sessionLengthMin,
    session_buffer: schedulingRules.session_buffer,
    travelOnsiteSurcharge: schedulingRules.travelOnsiteSurcharge ?? undefined,
    cancellationWindowHours:
      schedulingRules.cancellationWindowHours ?? undefined,
    noShowFee: schedulingRules.noShowFee ?? undefined,
    lateCancellationFee: schedulingRules.lateCancellationFee ?? undefined,
    service_radius:
      schedulingRules.inPersonSettings.serviceArea === 'radius_miles'
        ? (schedulingRules.inPersonSettings.radiusMiles ?? undefined)
        : undefined,
    service_zipcode: schedulingRules.inPersonSettings.zipCodes,
    requeried_report: reportNames,
    export_format: 'PDF',
    document,
    specility: specialtyNames,
    body_regions: bodyRegionNames,
    tags: visibilityMatching.keywords,
    accept_insurance_in_search: visibilityMatching.showInSearch,
    // show_in_search: visibilityMatching.showInSearch,
    // states: visibilityMatching.states,
    is_active: status === 'published',
    status,
    isDeleted: false,
  }

  return payload
}

export const createServicePackage = async (payload: ServicePackagePayload) => {
  const response = await api.post('/services', payload)
  return response.data
}

export const updateServicePackage = async (
  serviceId: string,
  payload: ServicePackagePayload
) => {
  const response = await api.put(`/services/${serviceId}`, payload)
  return response.data
}

export const submitServicePackage = async (
  formData: ServicePackageFormData,
  therapistId: string
) => {
  const payload = buildServicePackagePayload(formData, therapistId)

  if (formData.id) {
    return updateServicePackage(formData.id, payload)
  }

  return createServicePackage(payload)
}

export interface FetchServicePackagesParams {
  page?: number
  limit?: number
  search?: string
  status?: 'draft' | 'published' | 'archived' | 'all'
  speciality?: string
  therapistId?: string
}

export interface FetchServicePackagesResponse {
  services: ServicePackageListItem[]
  totalRecords?: number
  totalPages?: number
}

export const fetchServicePackages = async (
  params: FetchServicePackagesParams = {}
): Promise<FetchServicePackagesResponse> => {
  const { page = 1, limit = 10, search, status, speciality } = params

  const queryParams: Record<string, any> = {
    page,
    limit,
  }

  if (search?.trim()) {
    queryParams.search = search.trim()
  }

  if (status && status !== 'all') {
    queryParams.status = status
  }

  if (speciality?.trim()) {
    queryParams.speciality = speciality.trim()
  }

  const response = await api.get('/services', { params: queryParams })

  const services = Array.isArray(response.data?.services)
    ? response.data.services
    : []

  // Fetch specializations to resolve names
  const specializations = await fetchSpecializations()
  const specializationMap = new Map(
    specializations.map(spec => [spec.id, spec.name])
  )

  // Map API response to ServicePackageListItem
  const mappedServices: ServicePackageListItem[] = services.map((item: any) => {
    const formData = mapServiceResponseToFormData(item)

    const specializationData = item.specialization
    const specializationIdFromApi =
      typeof specializationData === 'object' && specializationData !== null
        ? specializationData._id || specializationData.id || ''
        : typeof specializationData === 'string'
          ? specializationData
          : ''

    const normalizedSpecializationId =
      specializationIdFromApi || formData.basics.discipline || 'unknown'

    const specializationNameFromApi =
      typeof specializationData === 'object' && specializationData !== null
        ? specializationData.name || specializationData.title || ''
        : typeof specializationData === 'string'
          ? specializationData
          : ''

    const resolvedSpecializationName =
      specializationNameFromApi ||
      specializationMap.get(normalizedSpecializationId) ||
      'Specialization not specified'

    const specializationDescription =
      (typeof specializationData === 'object' &&
        specializationData !== null &&
        (specializationData.description || '')) ||
      ''

    // Extract payer names from API response if available (new structure with objects)
    const insuranceData = item.insurances ?? item.acceptedPayers ?? []
    const payerNames = Array.isArray(insuranceData)
      ? insuranceData
        .map((insurance: any) => {
          // If it's an object with payer_name
          if (
            typeof insurance === 'object' &&
            insurance !== null &&
            insurance.payer_name
          ) {
            return insurance.payer_name
          }
          // If it's a string, try to map it to a name using payers list
          if (typeof insurance === 'string') {
            const payer = payers.find(
              p => p.id === insurance || p.name === insurance
            )
            return payer?.name || insurance
          }
          return null
        })
        .filter((name): name is string => Boolean(name))
      : []

    // Override acceptedPayers with payer names if we have them from the API
    if (payerNames.length > 0) {
      formData.pricingBilling.acceptedPayers = payerNames
    }

    return {
      id: formData.id || item._id || '',
      serviceTitle: formData.basics.serviceTitle || item.name || '',
      created_by:
        typeof item.created_by === 'string'
          ? item.created_by
          : typeof item.createdBy === 'string'
          ? item.createdBy
          : typeof item.created_by === 'object' && item.created_by !== null
          ? item.created_by.name || item.created_by.title || ''
          : undefined,
      specialization: {
        _id: normalizedSpecializationId,
        name: resolvedSpecializationName,
        description: specializationDescription,
      },
      specializationName: resolvedSpecializationName,
      modality: formData.basics.modality || [],
      shortDescription:
        formData.basics.shortDescription || item.short_description || '',
      privatePayRate:
        formData.pricingBilling.privatePayRate ?? item.private_pay_rate ?? null,
      sessionLengthMin:
        formData.schedulingRules.sessionLengthMin || item.session_length || 0,
      session_buffer:
        formData.schedulingRules.session_buffer || item.session_buffer || 0,
      status: formData.status || item.status || 'draft',
      updatedAt: formData.updatedAt || item.updatedAt || '',
      createdAt: formData.createdAt || item.createdAt || '',
      totalContracts: item.totalContracts ?? 0,
      totalSessions: item.totalSessions ?? 0,
      totalEarnings: item.totalEarnings || '$0.00',
      acceptInsuranceInSearch: formData.visibilityMatching.showInSearch,
      formData,
      service_radius: item.service_radius,
      place_of_service: item.place_of_service,
      // i want insurens name string
      insurances: item.insurances.map((insurance: any) => insurance.payer_name).join(', ') || '',
      // insurances: item.insurances?.map((insurance: any) => ({
      //   name: insurance.payer_name,
      // })) || [],
    }
  })

  return {
    services: mappedServices,
    totalRecords: response.data?.totalRecords || mappedServices.length,
    totalPages:
      response.data?.totalPages ||
      Math.ceil((response.data?.totalRecords || mappedServices.length) / limit),
  }
}

export const fetchServicePackagesAgencyProdiverId = async (
  params: FetchServicePackagesParams = {}
): Promise<FetchServicePackagesResponse> => {
  const { page = 1, limit = 10, search, status, speciality, therapistId } = params

  const queryParams: Record<string, any> = {
    page,
    limit,
  }

  if (search?.trim()) {
    queryParams.search = search.trim()
  }

  if (status && status !== 'all') {
    queryParams.status = status
  }

  if (speciality?.trim()) {
    queryParams.speciality = speciality.trim()
  }

  if (therapistId) {
    queryParams.therapistId = therapistId
  }

  const response = await api.get('/services', { params: queryParams })

  const services = Array.isArray(response.data?.services)
    ? response.data.services
    : []

  // Fetch specializations to resolve names
  const specializations = await fetchSpecializations()
  const specializationMap = new Map(
    specializations.map(spec => [spec.id, spec.name])
  )

  // Map API response to ServicePackageListItem
  const mappedServices: ServicePackageListItem[] = services.map((item: any) => {
    const formData = mapServiceResponseToFormData(item)

    const specializationData = item.specialization
    const specializationIdFromApi =
      typeof specializationData === 'object' && specializationData !== null
        ? specializationData._id || specializationData.id || ''
        : typeof specializationData === 'string'
          ? specializationData
          : ''

    const normalizedSpecializationId =
      specializationIdFromApi || formData.basics.discipline || 'unknown'

    const specializationNameFromApi =
      typeof specializationData === 'object' && specializationData !== null
        ? specializationData.name || specializationData.title || ''
        : typeof specializationData === 'string'
          ? specializationData
          : ''

    const resolvedSpecializationName =
      specializationNameFromApi ||
      specializationMap.get(normalizedSpecializationId) ||
      'Specialization not specified'

    const specializationDescription =
      (typeof specializationData === 'object' &&
        specializationData !== null &&
        (specializationData.description || '')) ||
      ''

    // Extract payer names from API response if available (new structure with objects)
    const insuranceData = item.insurances ?? item.acceptedPayers ?? []
    const payerNames = Array.isArray(insuranceData)
      ? insuranceData
        .map((insurance: any) => {
          // If it's an object with payer_name
          if (
            typeof insurance === 'object' &&
            insurance !== null &&
            insurance.payer_name
          ) {
            return insurance.payer_name
          }
          // If it's a string, try to map it to a name using payers list
          if (typeof insurance === 'string') {
            const payer = payers.find(
              p => p.id === insurance || p.name === insurance
            )
            return payer?.name || insurance
          }
          return null
        })
        .filter((name): name is string => Boolean(name))
      : []

    // Override acceptedPayers with payer names if we have them from the API
    if (payerNames.length > 0) {
      formData.pricingBilling.acceptedPayers = payerNames
    }

    return {
      id: formData.id || item._id || '',
      serviceTitle: formData.basics.serviceTitle || item.name || '',
      created_by:
        typeof item.created_by === 'string'
          ? item.created_by
          : typeof item.createdBy === 'string'
          ? item.createdBy
          : typeof item.created_by === 'object' && item.created_by !== null
          ? item.created_by.name || item.created_by.title || ''
          : undefined,
      specialization: {
        _id: normalizedSpecializationId,
        name: resolvedSpecializationName,
        description: specializationDescription,
      },
      specializationName: resolvedSpecializationName,
      modality: formData.basics.modality || [],
      shortDescription:
        formData.basics.shortDescription || item.short_description || '',
      privatePayRate:
        formData.pricingBilling.privatePayRate ?? item.private_pay_rate ?? null,
      sessionLengthMin:
        formData.schedulingRules.sessionLengthMin || item.session_length || 0,
      session_buffer:
        formData.schedulingRules.session_buffer || item.session_buffer || 0,
      status: formData.status || item.status || 'draft',
      updatedAt: formData.updatedAt || item.updatedAt || '',
      createdAt: formData.createdAt || item.createdAt || '',
      totalContracts: item.totalContracts ?? 0,
      totalSessions: item.totalSessions ?? 0,
      totalEarnings: item.totalEarnings || '$0.00',
      acceptInsuranceInSearch: formData.visibilityMatching.showInSearch,
      formData,
      service_radius: item.service_radius,
      place_of_service: item.place_of_service,
      // i want insurens name string
      insurances: item.insurances.map((insurance: any) => insurance.payer_name).join(', ') || '',
      // insurances: item.insurances?.map((insurance: any) => ({
      //   name: insurance.payer_name,
      // })) || [],
    }
  })

  return {
    services: mappedServices,
    totalRecords: response.data?.totalRecords || mappedServices.length,
    totalPages:
      response.data?.totalPages ||
      Math.ceil((response.data?.totalRecords || mappedServices.length) / limit),
  }
}

export const deleteServicePackage = async (serviceId: string) => {
  await api.delete(`/services/${serviceId}`)
}

export const updateServicePackageVisibility = async (
  serviceId: string,
  isVisible: boolean
) => {
  const response = await api.put(`/services/${serviceId}`, {
    accept_insurance_in_search: isVisible,
  })
  return response.data
}

export const servicePackageService = {
  buildPayload: buildServicePackagePayload,
  create: createServicePackage,
  update: updateServicePackage,
  submit: submitServicePackage,
  mapToFormData: mapServiceResponseToFormData,
  fetchCptCodes,
  fetchIcdCodes,
  fetchHCPCSCodes,
  fetchModifiers,
  fetchSpecializations,
  fetchServicePackages,
  delete: deleteServicePackage,
  updateVisibility: updateServicePackageVisibility,
}

export interface UploadedContractDocument {
  fileName: string
  signedUrl: string
  location: string
}

export const uploadContractDocuments = async (
  files: File[]
): Promise<UploadedContractDocument[]> => {
  if (!files.length) {
    return []
  }

  const formData = new FormData()
  files.forEach(file => {
    formData.append('files', file)
  })

  const response = await api.post('/therapist/upload-multiple', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 0,
  })

  const uploadedFiles = Array.isArray(response.data?.files)
    ? response.data.files
    : []

  return uploadedFiles
    .map((file: any) => ({
      fileName: file?.fileName ?? '',
      signedUrl: file?.signedUrl ?? '',
      location: file?.location ?? '',
    }))
    .filter(
      (file: UploadedContractDocument) =>
        file.fileName || file.signedUrl || file.location
    )
}
